import _, { last } from 'lodash';
import * as React from 'react';
import { animated, useSpring } from 'react-spring';
import { projectsApiService } from '../../../../graphql/projects/projectsApiService';
import { formatCurrency } from '../../../../logic/otherUtilities/formatting/formatCurrency';
import AnalysisChartLoading from '../../AnalysisChartLoading';

import { makeStyles } from '@material-ui/core';
import { formatNumber } from '../../../../logic/otherUtilities/formatting/formatNumber';
import { formatTemplate } from '../../../../logic/otherUtilities/formatting/formatTemplate';
import { ChartProps } from '../../AnalysisList';
import { ColorPalette } from '../../Palette/ColorPalette';
import { adjustTextColor } from '../../Palette/adjustTextColor';
import { internationalText } from '../../internationalText';
import DataTimedOut, { didQueryTimeout } from '../DataTimedOut';
import NotEnoughData from '../NotEnoughData';

export interface MarketPotentialProps extends ChartProps {}

export const useClasses = makeStyles<{}, ColorPalette>({
  circle1: {
    color: (palette) => adjustTextColor(palette, 0.85, 1),
    backgroundColor: (palette) => palette.primaryScale[2],
  },
  circle2: {
    color: (palette) => adjustTextColor(palette, 0.85, 1),
    backgroundColor: (palette) => palette.primaryScale[1],
  },
  circle3: {
    color: (palette) => adjustTextColor(palette, 0.85, 1),
    backgroundColor: (palette) => palette.primaryScale[0],
  },
  circle4: {
    color: (palette) => adjustTextColor(palette, 0.85, 1),
    backgroundColor: (palette) => palette.tertiaryScale[2],
  },
});

const MarketPotential: React.FC<MarketPotentialProps> = ({
  project,
  analysisFilterState,
  setAnalysisFilterState,
  palette,
  language,
  skipAnimation,
  emptyOnFail,
}) => {
  const classes = useClasses(palette);
  // Adjust shown filters
  React.useEffect(
    () =>
      setAnalysisFilterState((prev) => ({
        ...prev,
        shownFields: {
          showSerpOnly: true,
          showTargetRankValues: true,
          showConversionRate: true,
          showAverageOrderValue: true,
          showCurrentResult: true,
          showTagGroupId: true,
          showTagId: true,
        },
      })),
    []
  );

  const groupSequences = (array: number[]) => {
    return _.reduce(
      array,
      function (result, value, index, collection) {
        if (value - collection[index - 1] === 1) {
          const group = last(result);
          group.push(value);
        } else {
          result.push([value]);
        }
        return result;
      },
      []
    );
  };

  const selectedRanks = (array: number[]) => {
    let sequences = groupSequences(array.sort((a, b) => a - b));
    let stringList = sequences.map((x: number[]) => {
      if (x.length > 1) {
        return Math.min(...x).toString() + '-' + Math.max(...x).toString();
      } else return x.toString();
    });
    return stringList.join(', ');
  };

  // Data fetching
  const dataQuery = projectsApiService.queries.analysis.useGetMarketPotential({
    projectId: project.id,
    serpOnly: analysisFilterState?.serpOnly,
    rankValues: analysisFilterState?.targetRankValues,
    tagGroupId: analysisFilterState?.tagGroupId,
    tagId: analysisFilterState?.tagId,
  });

  const dataLoading = dataQuery?.query?.loading;
  const dataResult = dataQuery?.result ?? null;

  const averageRankCtr = dataResult?.averageRankCtr ?? 0;
  const rankString = selectedRanks(analysisFilterState?.targetRankValues as number[]);

  const conversionRate =
    Number(analysisFilterState?.conversionRate) > 0
      ? Number(analysisFilterState?.conversionRate) / 100
      : 0;

  const averageOrderValue =
    Number(analysisFilterState?.averageOrderValue) > 0
      ? Number(analysisFilterState?.averageOrderValue)
      : 0;

  const normalNumbers = {
    searchVolume: dataResult?.projectSearchVolume ?? 0,
    trafficPotential: dataResult?.projectTrafficPotential ?? 0,
    trafficToday: dataResult?.projectTrafficToday ?? 0,
    conversions: conversionRate * (dataResult?.projectTrafficPotential ?? 0),
    conversionsToday: conversionRate * (dataResult?.projectTrafficToday ?? 0),
    revenue: conversionRate * (dataResult?.projectTrafficPotential ?? 0) * averageOrderValue,
    revenueToday: conversionRate * (dataResult?.projectTrafficToday ?? 0) * averageOrderValue,
  };

  const localSkipAnimation = skipAnimation || sessionStorage.getItem('skip_animation'); // Setting for skipping spring when screenshotting
  const springNumbers = useSpring({
    ...normalNumbers,
    from: {
      searchVolume: 0,
      trafficPotential: 0,
      trafficToday: 0,
      conversions: 0,
      conversionsToday: 0,
      revenue: 0,
      revenueToday: 0,
    },
    immediate: localSkipAnimation ? true : false,
  });

  const showTraffic = dataResult?.projectTrafficPotential >= 0;
  const showConversions = conversionRate > 0;
  const showRevenue = averageOrderValue > 0;

  // Styling
  const slideTheme = analysisFilterState?.theme;
  const fontFamily = analysisFilterState?.theme === 'voxel' ? 'font-sans' : 'font-pixel-charts';

  if (didQueryTimeout(dataQuery?.query)) {
    return emptyOnFail ? null : <DataTimedOut />;
  }

  if (dataLoading && !dataResult) {
    return <AnalysisChartLoading />;
  }

  if (!(dataResult?.projectSearchVolume > 0)) {
    return emptyOnFail ? null : <NotEnoughData />;
  }

  return (
    <div data-screenshot-id="chart-loaded">
      <div className="flex flex-row justify-center h-[500px]">
        <div
          className={`relative ${
            showConversions && showRevenue
              ? 'w-[1100px]'
              : showConversions
              ? 'w-[800px]'
              : 'w-[650px]'
          }`}
        >
          <div
            className={`
              h-[400px] w-[400px]
              rounded-full
              absolute
              left-0
              top-[50px]
              flex flex-col items-center justify-center
              p-4
              ${classes.circle1}
            `}
          >
            <div
              className={`
                      pb-1 pt-0
                      ${fontFamily}
                      font-semibold
                      text-5xl
                    `}
            >
              <animated.span>
                {springNumbers.searchVolume.to((val: number) => formatNumber(val, 0))}
              </animated.span>
            </div>
            <div className="text-center text-base">
              {internationalText.chart.market_potential.searches.label[language]}
            </div>
          </div>

          {showTraffic && (
            <>
              <div
                className={`
                  h-[250px] w-[250px]
                  rounded-full
                  absolute
                  left-[360px]
                  top-[40px]
                  flex flex-col items-center justify-center
                  p-4
                  ${classes.circle2}
                `}
              >
                <div
                  className={`
                      pb-1 pt-0
                      font-bold
                      ${fontFamily}
                      text-regular
                      text-3xl
                    `}
                >
                  <animated.span>
                    {springNumbers.trafficPotential.to((val: number) => formatNumber(val, 0))}
                  </animated.span>
                </div>
                <div className="text-center text-base pt-2">
                  {internationalText.chart.market_potential.traffic.label[language]}
                </div>
                <div className="text-center text-base pt-2">
                  {formatTemplate(
                    internationalText.chart.market_potential.traffic.desc_1[language],
                    [rankString]
                  )}
                </div>
                <div className="text-center text-base pt-2">
                  {formatTemplate(
                    internationalText.chart.market_potential.traffic.desc_2[language],
                    [averageRankCtr.toFixed(1) + '%']
                  )}
                </div>
                {analysisFilterState?.currentResult && (
                  <div
                    className={`
                       pb-0 pt-72
                       font-bold
                       ${fontFamily}
                       text-regular
                       text-gray-600
                       absolute
               `}
                  >
                    {internationalText.chart.market_potential.traffic.desc_3[language]}{' '}
                    <animated.span>
                      {springNumbers.trafficToday.to((val: number) => formatNumber(val, 0))}
                    </animated.span>
                  </div>
                )}
              </div>
            </>
          )}
          {showTraffic && showConversions && (
            <div
              className={`
                h-[200px] w-[200px]
                rounded-full
                absolute
                left-[550px]
                top-[180px]
                flex flex-col items-center justify-center
                p-4
                ${classes.circle3}
              `}
            >
              <div
                className={`
                  pb-2 pt-0
                  font-bold
                  ${fontFamily}
                  text-regular
                  text-3xl
                `}
              >
                <animated.span>
                  {springNumbers.conversions.to((val: number) => formatNumber(val, 0))}
                </animated.span>
              </div>
              <div className="text-center text-base pt-0.5">
                {internationalText.chart.market_potential.conversions.label[language]}
              </div>
              <div className="text-center text-base pt-2">
                {formatTemplate(
                  internationalText.chart.market_potential.conversions.desc_1[language],
                  [(conversionRate * 100).toFixed(1) + '%']
                )}
              </div>
              {analysisFilterState?.currentResult && (
                <div
                  className={`
                       pb-0 pt-60
                       font-bold
                       ${fontFamily}
                       text-regular
                       text-gray-600
                       absolute
               `}
                >
                  {internationalText.chart.market_potential.traffic.desc_3[language]}{' '}
                  <animated.span>
                    {springNumbers.conversionsToday.to((val: number) => formatNumber(val, 0))}
                  </animated.span>
                </div>
              )}
            </div>
          )}
          {showTraffic && showConversions && showRevenue && (
            <div
              className={`
                      h-[200px] w-[200px]
                      rounded-full
                      absolute
                      left-[720px]
                      top-[230px]
                      flex flex-col items-center justify-center
                      p-4
                      ${classes.circle4}
                    `}
            >
              <div
                className={`
                  pb-0 pt-2
                  font-bold
                  ${fontFamily}
                  text-regular
                  text-2xl
                `}
              >
                <animated.span>
                  {springNumbers.revenue.to((val: number) => formatNumber(val, 0))}
                </animated.span>
              </div>
              <div className="text-center text-base pt-2">{project.currency}</div>
              <div className="text-center text-base pt-2">
                {formatTemplate(internationalText.chart.market_potential.revenue.desc_1[language], [
                  formatCurrency(project.currency, averageOrderValue),
                ])}
              </div>
              {analysisFilterState?.currentResult && (
                <div
                  className={`
                      pb-0 pt-60
                      font-bold
                      ${slideTheme === 'voxel' ? 'font-voxel-charts' : 'font-pixel-charts'}
                      text-regular
                      text-gray-600
                      absolute
              `}
                >
                  {internationalText.chart.market_potential.traffic.desc_3[language]}{' '}
                  <animated.span>
                    {springNumbers.revenueToday.to((val: number) =>
                      formatCurrency(project.currency, formatNumber(val, 0))
                    )}
                  </animated.span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarketPotential;
